import { useMemo } from 'react';
import useConfig from '../useConfig';
import { useLocation, Link, matchPath } from 'react-router-dom';
import type { ISidebarItem } from '../../types/sidebar';
import type { MenuProps } from 'antd';
import { AntIcon } from '../../components';

export const getSelectedKey = (
  items: ISidebarItem[],
  currentPath: string
): string | undefined => {
  const traverse = (items: ISidebarItem[]): string | undefined => {
    for (const item of items) {
      if (
        item.route &&
        matchPath({ path: item.route.path, end: false }, currentPath)
      ) {
        return item.id;
      }
      if (item.children) {
        const found = traverse(item.children);
        if (found) return found;
      }
    }
    return undefined;
  };

  return traverse(items);
};

export const convertToMenuItems = (
  items: ISidebarItem[],
  location: string
): NonNullable<MenuProps['items']> => {
  return items
    .sort((a, b) => a.order - b.order)
    .map(item => {
      const hasChildren = item.children && item.children.length > 0;

      const menuItem = {
        key: item.id,
        icon: item.icon ? <AntIcon name={item.icon} /> : null,
        label: item.route ? (
          <Link to={item.route.path}>{item.label}</Link>
        ) : (
          item.label
        ),
        ...(hasChildren && {
          children: convertToMenuItems(item.children!, location),
        }),
      };

      return menuItem;
    });
};

export const getDefaultOpenKeys = (items: ISidebarItem[]): string[] => {
  const openKeys: string[] = [];

  const traverse = (items: ISidebarItem[]) => {
    items.forEach(item => {
      if (item.defaultOpen) {
        openKeys.push(item.id);
      }
      if (item.children) {
        traverse(item.children);
      }
    });
  };

  traverse(items);

  return openKeys;
};

export const useBuildSidebarMenuItems = () => {
  const { sidebar } = useConfig();

  const location = useLocation();

  const menuItems = useMemo(
    () => convertToMenuItems(sidebar, location.pathname),
    [sidebar, location.pathname]
  );

  const defaultOpenKeys = useMemo(() => getDefaultOpenKeys(sidebar), [sidebar]);

  const selectedKey = useMemo(
    () => getSelectedKey(sidebar, location.pathname),
    [sidebar, location.pathname]
  );

  return {
    menuItems,
    defaultOpenKeys,
    selectedKey,
  };
};

export default useBuildSidebarMenuItems;
